import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, UserCircle2, UserX, UserCheck, Star, ArrowUp, ArrowDown, Eye, Images, Shuffle, Clock, Layers, History, Pencil, DoorOpen, Baby, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import BulkPhotoUploadDialog from "@/components/BulkPhotoUploadDialog";
import EditUserDialog from "@/components/EditUserDialog";
import AssignChildrenDialog from "@/components/AssignChildrenDialog";

const emptyUser = { email: "", password: "", name: "", role: "teacher", phone: "", specialty: "", photo_url: "" };

function initials(name) {
  const parts = (name || "").trim().split(/\s+/);
  return (parts[0]?.[0] || "؟") + (parts[1]?.[0] || "");
}

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function StaffPage() {
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("teacher");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyUser);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [teamPreview, setTeamPreview] = useState([]);
  const [featuredCap, setFeaturedCap] = useState(8);
  const [selected, setSelected] = useState(new Set());
  const [highlightId, setHighlightId] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [rotationPreview, setRotationPreview] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [rotationHistory, setRotationHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [assigningTeacher, setAssigningTeacher] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/users?role=${tab}`);
    setUsers(data);
  };
  useEffect(() => { load(); }, [tab]);

  // Auto-refresh when tab regains focus / becomes visible again
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", load);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", load);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const loadTeamPreview = async () => {
    try {
      const [pt, ls, rot, hist] = await Promise.all([
        api.get("/public/team"),
        api.get("/public/landing-settings"),
        api.get("/admin/team-rotation/preview").catch(() => ({ data: null })),
        api.get("/admin/rotation-history?limit=4").catch(() => ({ data: [] })),
      ]);
      setTeamPreview(pt.data || []);
      setFeaturedCap(parseInt(ls.data?.featured_team_max || 8, 10));
      setRotationPreview(rot.data || null);
      setRotationHistory(hist.data || []);
    } catch { /* silent */ }
  };

  useEffect(() => { if (tab === "teacher") loadTeamPreview(); }, [tab, users]);

  const toggleRotationPool = async (u) => {
    try {
      await api.patch(`/users/${u.id}/rotation-pool`, { in_pool: !u.in_rotation_pool });
      toast.success(!u.in_rotation_pool ? "أُضيف إلى دورة التدوير" : "أُزيل من دورة التدوير");
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const rotateNow = async () => {
    if (!confirm("تدوير المعلمين المميّزين الآن؟ سيتم استبدال المميّزين الحاليين بمن لم يظهروا مؤخراً من داخل الدورة.")) return;
    setRotating(true);
    try {
      const { data } = await api.post("/admin/team-rotation/rotate");
      toast.success(`تم تدوير ${data.rotated} معلمين`);
      await load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setRotating(false); }
  };

  const updateFeaturedCap = async (v) => {
    setFeaturedCap(v);
    try {
      await api.put("/admin/landing-settings", { featured_team_max: v });
      toast.success("تم تحديث حد المعلمين المميّزين");
      await loadTeamPreview();
    } catch (e) { toast.error(extractError(e)); }
  };

  const save = async () => {
    try {
      await api.post("/users", form);
      toast.success("تم إضافة الحساب");
      setOpen(false);
      setForm({ ...emptyUser, role: tab });
      await load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}/${u.active ? "deactivate" : "activate"}`);
      toast.success(u.active ? "تم إلغاء الوصول" : "تم إعادة التفعيل");
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const toggleFeatured = async (u) => {
    try {
      await api.patch(`/users/${u.id}/featured`, { featured: !u.featured_on_landing });
      toast.success(!u.featured_on_landing ? "سيظهر في الصفحة الرئيسية" : "أُخفي من الصفحة الرئيسية");
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const moveInLanding = async (u, direction, isUndo = false) => {
    try {
      const { data } = await api.post(`/users/${u.id}/move-in-landing`, { direction });
      setHighlightId(u.id);
      setTimeout(() => setHighlightId(null), 2000);
      await load();
      if (!isUndo && data?.moved) {
        const reverse = direction === "up" ? "down" : "up";
        toast(`تم نقل ${u.name} ${direction === "up" ? "لأعلى" : "لأسفل"}`, {
          duration: 5000,
          action: {
            label: "تراجع",
            onClick: () => moveInLanding(u, reverse, true),
          },
        });
      }
    } catch (e) { toast.error(extractError(e)); }
  };

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const bulkFeature = async (featured) => {
    const ids = users.filter((u) => u.role === "teacher" && selected.has(u.id)).map((u) => u.id);
    if (ids.length === 0) { toast.error("لم يتم تحديد أي معلم"); return; }
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await api.patch(`/users/${id}/featured`, { featured });
        ok++;
      } catch { fail++; }
    }
    toast.success(`تم: ${ok}${fail ? ` — فشل: ${fail} (قد يكون الحد ممتلئاً)` : ""}`);
    setSelected(new Set());
    await load();
  };

  const onFile = async (u, file) => {
    if (!file) return;
    if (file.size > 1_400_000) { toast.error("حجم الصورة كبير — يفضّل أقل من 1MB"); return; }
    setUploadingFor(u.id);
    try {
      const dataUrl = await fileToDataUrl(file);
      await api.patch(`/users/${u.id}/photo`, { photo_url: dataUrl });
      toast.success("تم تحديث الصورة");
      await load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setUploadingFor(null); }
  };

  return (
    <div className="space-y-6" data-testid="staff-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900">الموظفون وأولياء الأمور</h1>
          <p className="text-slate-500 mt-2">إدارة حسابات المعلمين والأهل</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {tab === "teacher" && (
            <Button onClick={() => setBulkOpen(true)} variant="outline" className="h-12 gap-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="bulk-upload-photos-btn">
              <Images className="w-4 h-4" /> رفع صور دفعة واحدة
            </Button>
          )}
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setForm({ ...emptyUser, role: tab }); }}>
          <DialogTrigger asChild>
            <Button className="h-12 gap-2 bg-sky-600 hover:bg-sky-700" data-testid="add-user-btn">
              <Plus className="w-4 h-4" /> إضافة حساب
            </Button>
          </DialogTrigger>          <DialogContent>
            <DialogHeader><DialogTitle className="text-2xl font-black">حساب جديد</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-1.5">
                <Label className="font-bold">الدور</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="teacher">معلم / معالج</SelectItem>
                    <SelectItem value="parent">ولي أمر</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="font-bold">الاسم</Label>
                <Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="font-bold">البريد الإلكتروني</Label>
                <Input data-testid="user-email" dir="ltr" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="font-bold">كلمة المرور</Label>
                <Input data-testid="user-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="font-bold">رقم الهاتف (للواتساب)</Label>
                <Input data-testid="user-phone" dir="ltr" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              {form.role === "teacher" && (
                <div className="space-y-1.5">
                  <Label className="font-bold">التخصص</Label>
                  <Input data-testid="user-specialty" value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} placeholder="تخاطب، وظيفي، سلوكي..." />
                </div>
              )}
              <Button data-testid="save-user-btn" onClick={save} className="w-full h-12 bg-sky-600 hover:bg-sky-700">حفظ</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <BulkPhotoUploadDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        teachers={users.filter((u) => u.role === "teacher")}
        onDone={load}
      />
      <EditUserDialog
        open={!!editingUser}
        onClose={() => setEditingUser(null)}
        user={editingUser}
        onSaved={load}
      />
      <AssignChildrenDialog
        open={!!assigningTeacher}
        onClose={() => setAssigningTeacher(null)}
        teacher={assigningTeacher}
      />

      <div className="inline-flex bg-white rounded-xl p-1 border border-slate-200">
        <TabBtn active={tab === "teacher"} onClick={() => setTab("teacher")} testid="tab-teachers">المعلمون</TabBtn>
        <TabBtn active={tab === "parent"} onClick={() => setTab("parent")} testid="tab-parents">أولياء الأمور</TabBtn>
      </div>

      {tab === "teacher" && (
        <Card className="border-2 border-amber-100 overflow-hidden" data-testid="team-preview-card">
          <div className="h-1 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500" />
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center">
                  <Eye className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-black text-slate-900">معاينة الفريق في الصفحة الرئيسية</h2>
                  <p className="text-xs text-slate-500">هذا ما سيراه الأهل — أعِد الترتيب من أزرار "أعلى/أسفل" داخل بطاقة كل معلم</p>
                </div>
              </div>
              <div className="flex items-center gap-2 border rounded-lg bg-amber-50 border-amber-200 px-3 h-10">
                <Star className="w-3.5 h-3.5 text-amber-600 fill-amber-500" />
                <span className="text-xs font-black text-amber-700">حد المميّزين</span>
                <select
                  value={featuredCap}
                  onChange={(e) => updateFeaturedCap(parseInt(e.target.value, 10))}
                  className="bg-transparent text-xs font-black text-amber-800 outline-none"
                  data-testid="featured-team-max-select"
                >
                  {[1,2,3,4,5,6,7,8,10,12,15,20].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="text-[11px] text-amber-600">({teamPreview.length}/{featuredCap})</span>
              </div>
            </div>
            {teamPreview.length === 0 ? (
              <div className="text-center py-6 text-slate-400 text-sm bg-slate-50 rounded-xl">لا يوجد معلمون مميّزون بعد — ضع نجمة على من تريد إظهاره</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="team-preview-grid">
                {teamPreview.map((m, i) => (
                  <div key={m.id} className={`text-center bg-white rounded-2xl border p-3 relative transition-all ${highlightId === m.id ? "border-amber-400 ring-4 ring-amber-200 scale-105 shadow-lg" : "border-slate-100"}`} data-testid={`team-preview-${i}`}>
                    <div className="absolute top-1 right-1 w-6 h-6 rounded-full bg-amber-500 text-white text-[10px] font-black flex items-center justify-center shadow">
                      {i + 1}
                    </div>
                    <div className="w-16 h-16 mx-auto rounded-2xl overflow-hidden shadow mb-2 bg-gradient-to-br from-sky-100 to-blue-100">
                      {m.photo_url ? (
                        <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-sky-500 to-blue-600 text-white flex items-center justify-center font-black text-lg">
                          {(m.name || "?").trim()[0]}
                        </div>
                      )}
                    </div>
                    <div className="text-xs font-black text-slate-800 line-clamp-2 leading-tight">{m.name}</div>
                    {m.specialty && <div className="text-[10px] text-slate-500 mt-1 line-clamp-1">{m.specialty}</div>}
                  </div>
                ))}
              </div>
            )}

            {/* Rotation controls */}
            <div className="mt-5 pt-4 border-t border-amber-100 space-y-3" data-testid="rotation-controls">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center">
                    <Shuffle className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-sm font-black text-slate-900">تدوير المعلمين المميّزين</div>
                    <div className="text-[11px] text-slate-500">
                      في المجموعة: <span className="font-black">{rotationPreview?.pool_size ?? 0}</span> · الحد الأقصى للعرض: {rotationPreview?.cap ?? featuredCap}
                    </div>
                  </div>
                </div>
                <Button
                  onClick={rotateNow}
                  disabled={rotating || !rotationPreview?.pool_size}
                  className="gap-2 bg-gradient-to-l from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                  data-testid="rotate-now-btn"
                >
                  <Shuffle className="w-4 h-4" /> {rotating ? "جارٍ التدوير..." : "دوّر الآن"}
                </Button>
                <Button
                  onClick={() => setHistoryOpen((v) => !v)}
                  variant="outline"
                  className="gap-2 text-indigo-700 border-indigo-300"
                  data-testid="rotation-history-toggle"
                >
                  <History className="w-4 h-4" /> السجل ({rotationHistory.length})
                </Button>
              </div>
              {historyOpen && (
                <div className="rounded-2xl border-2 border-indigo-100 bg-indigo-50/40 p-3 space-y-2" data-testid="rotation-history-panel">
                  <div className="text-xs font-black text-indigo-700 flex items-center gap-1"><History className="w-3.5 h-3.5" /> آخر {rotationHistory.length} عمليات تدوير</div>
                  {rotationHistory.length === 0 ? (
                    <div className="text-[11px] text-slate-500 text-center py-2">لا يوجد سجل حتى الآن — نفّذ أوّل تدوير</div>
                  ) : rotationHistory.map((h) => {
                    const d = new Date(h.rotated_at);
                    const dateStr = d.toLocaleString("ar", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
                    return (
                      <div key={h.id} className="rounded-xl bg-white border border-indigo-100 p-2.5" data-testid={`rotation-history-${h.id}`}>
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
                          <div className="flex items-center gap-2 text-[11px]">
                            <span className="font-black text-slate-700">{dateStr}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[9px] font-black ${h.source === "cron" ? "bg-emerald-100 text-emerald-700" : "bg-indigo-100 text-indigo-700"}`}>
                              {h.source === "cron" ? "تلقائي (Cron)" : "يدوي"}
                            </span>
                            {h.triggered_by_name && <span className="text-slate-400 text-[10px]">بواسطة {h.triggered_by_name}</span>}
                          </div>
                          <span className="text-[10px] font-black text-indigo-600">{h.count} معلمين</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {h.teachers.slice(0, 8).map((t) => (
                            <span key={t.id} className="inline-flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5 text-[10px] font-bold text-slate-700">
                              {t.photo_url ? <img src={t.photo_url} alt="" className="w-4 h-4 rounded-full object-cover" /> : <span className="w-4 h-4 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[8px]">{(t.name || "?")[0]}</span>}
                              {t.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {rotationPreview?.next_batch?.length > 0 ? (
                <div>
                  <div className="text-[11px] font-black text-slate-500 mb-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> المعلمون التاليون في الدور (الأقل ظهوراً أولاً):
                  </div>
                  <div className="flex flex-wrap gap-2" data-testid="next-in-line">
                    {rotationPreview.next_batch.slice(0, 8).map((m) => (
                      <div key={m.id} className="inline-flex items-center gap-2 bg-white border border-indigo-200 rounded-full ps-1 pe-3 py-1">
                        <div className="w-6 h-6 rounded-full overflow-hidden bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center text-[10px] font-black shrink-0">
                          {m.photo_url ? <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover" /> : (m.name || "?")[0]}
                        </div>
                        <span className="text-[11px] font-black text-slate-700">{m.name}</span>
                        {!m.last_featured_at && <span className="text-[9px] text-emerald-700 bg-emerald-100 rounded px-1">جديد</span>}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-[11px] text-slate-500 bg-slate-50 rounded-lg p-2 text-center">
                  أضف معلمين إلى دورة التدوير باستخدام <Layers className="w-3 h-3 inline" /> في بطاقات المعلمين لتفعيل الزر.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {tab === "teacher" && selected.size > 0 && (
        <div className="sticky top-2 z-20 bg-white/95 backdrop-blur border-2 border-indigo-200 rounded-2xl p-3 flex items-center gap-2 flex-wrap shadow-lg" data-testid="bulk-actions-bar">
          <div className="font-black text-sm text-indigo-700">تم تحديد {selected.size} معلم</div>
          <div className="ms-auto flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setSelected(new Set())} variant="outline" data-testid="bulk-clear">
              إلغاء التحديد
            </Button>
            <Button size="sm" onClick={() => setSelected(new Set(users.filter((u) => u.role === "teacher").map((u) => u.id)))} variant="outline" data-testid="bulk-select-all">
              تحديد كل المعلمين
            </Button>
            <Button size="sm" onClick={() => bulkFeature(false)} variant="outline" className="text-slate-700 gap-1" data-testid="bulk-unstar">
              <Star className="w-3.5 h-3.5" /> إلغاء تمييز
            </Button>
            <Button size="sm" onClick={() => bulkFeature(true)} className="bg-gradient-to-l from-amber-500 to-orange-600 gap-1" data-testid="bulk-star">
              <Star className="w-3.5 h-3.5 fill-white" /> تمييز الكل
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {users.map((u) => (
          <Card key={u.id} data-testid={`user-card-${u.id}`} className={`card-elevated border-slate-200/70 ${selected.has(u.id) ? "ring-4 ring-indigo-300" : ""}`}>
            <CardContent className="p-6">
              {u.role === "teacher" && (
                <label className="flex items-center gap-2 mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.has(u.id)}
                    onChange={() => toggleSelected(u.id)}
                    className="w-4 h-4 accent-indigo-600"
                    data-testid={`select-user-${u.id}`}
                  />
                  <span className="text-xs font-bold text-slate-500">اختيار للإجراءات المجمّعة</span>
                </label>
              )}
              <div className="flex items-start gap-4">
                <label className="relative w-14 h-14 rounded-2xl overflow-hidden cursor-pointer group shrink-0" data-testid={`upload-photo-${u.id}`}>
                  {u.photo_url ? (
                    <img src={u.photo_url} alt={u.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-sky-400 to-blue-500 text-white flex items-center justify-center font-black text-lg">
                      {initials(u.name)}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] font-bold">
                    {uploadingFor === u.id ? "..." : "تغيير"}
                  </div>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(u, e.target.files?.[0])} data-testid={`photo-input-${u.id}`} />
                </label>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-slate-900 text-lg truncate">{u.name}</div>
                  <div className="text-xs text-slate-500 truncate" dir="ltr">{u.email}</div>
                  {u.phone && <div className="text-xs text-slate-500 mt-1" dir="ltr">{u.phone}</div>}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {u.specialty && <Badge variant="secondary">{u.specialty}</Badge>}
                    {u.role === "teacher" && u.room_number && (
                      <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 gap-1" data-testid={`room-badge-${u.id}`}>
                        <DoorOpen className="w-3 h-3" /> قاعة {u.room_number}
                      </Badge>
                    )}
                  </div>
                </div>
                {u.active ? (
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">نشط</Badge>
                ) : (
                  <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">معطّل</Badge>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 text-sky-700 border-sky-200 hover:bg-sky-50"
                  onClick={() => setEditingUser(u)}
                  data-testid={`edit-user-${u.id}`}
                >
                  <Pencil className="w-4 h-4" /> تعديل
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={`gap-2 ${u.active ? "text-rose-700" : "text-emerald-700"}`}
                  onClick={() => toggleActive(u)}
                  data-testid={`toggle-user-${u.id}`}
                >
                  {u.active ? <><UserX className="w-4 h-4" />إلغاء</> : <><UserCheck className="w-4 h-4" />تفعيل</>}
                </Button>
              </div>
              {u.role === "teacher" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-2 gap-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                  onClick={() => setAssigningTeacher(u)}
                  data-testid={`assign-children-${u.id}`}
                >
                  <Baby className="w-4 h-4" /> إسناد أطفال إلى هذا المعلم
                </Button>
              )}
              {u.role === "teacher" && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className={`w-full mt-2 gap-2 ${u.featured_on_landing ? "text-amber-700 border-amber-300 bg-amber-50" : "text-slate-600"}`}
                    onClick={() => toggleFeatured(u)}
                    data-testid={`toggle-featured-${u.id}`}
                  >
                    <Star className={`w-4 h-4 ${u.featured_on_landing ? "fill-amber-400" : ""}`} />
                    {u.featured_on_landing ? "معروض في الصفحة الرئيسية" : "عرض في الصفحة الرئيسية"}
                  </Button>
                  {u.featured_on_landing && (
                    <div className="flex gap-1 mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 gap-1 h-8 text-amber-700 border-amber-200"
                        onClick={() => moveInLanding(u, "up")}
                        title="نقل لأعلى في الصفحة الرئيسية"
                        data-testid={`move-landing-up-${u.id}`}
                      >
                        <ArrowUp className="w-3 h-3" /> أعلى
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 gap-1 h-8 text-amber-700 border-amber-200"
                        onClick={() => moveInLanding(u, "down")}
                        title="نقل لأسفل في الصفحة الرئيسية"
                        data-testid={`move-landing-down-${u.id}`}
                      >
                        <ArrowDown className="w-3 h-3" /> أسفل
                      </Button>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className={`w-full mt-2 gap-2 ${u.in_rotation_pool ? "text-indigo-700 border-indigo-300 bg-indigo-50" : "text-slate-600"}`}
                    onClick={() => toggleRotationPool(u)}
                    data-testid={`toggle-rotation-${u.id}`}
                  >
                    <Shuffle className={`w-4 h-4 ${u.in_rotation_pool ? "text-indigo-600" : ""}`} />
                    {u.in_rotation_pool ? "ضمن دورة التدوير" : "أضف إلى دورة التدوير"}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        ))}
        {users.length === 0 && (
          <Card className="md:col-span-2 lg:col-span-3"><CardContent className="p-16 text-center text-slate-400">لا يوجد حسابات</CardContent></Card>
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-6 py-2.5 rounded-lg font-bold text-sm transition-all ${active ? "bg-sky-600 text-white" : "text-slate-600 hover:text-slate-900"}`}
    >
      {children}
    </button>
  );
}
